import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import {
    adminListAllowlist, adminAddAllowlist, adminDeleteAllowlist,
} from "@/lib/api";
import { Card, SectionTitle, Spinner, EmptyState, Table, StatusPill, KawaiiModal, ConfirmDialog } from "./kit";

const fmtDate = (v) => {
    if (!v) return "—";
    try { return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return v; }
};

export default function AllowlistSection() {
    const [rows, setRows] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [text, setText] = useState("");
    const [saving, setSaving] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await adminListAllowlist();
            setRows(r.allowlist || []);
        } catch { /* auth gate */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    const add = async () => {
        const emails = text.split(/[\n,]/).map((e) => e.trim()).filter(Boolean);
        if (emails.length === 0) { toast.error("paste at least one email 🍡"); return; }
        setSaving(true);
        try {
            const r = await adminAddAllowlist(emails.length === 1 ? emails[0] : emails);
            const added = (r.added || []).length;
            const skipped = (r.skipped || []).length;
            toast.success(`added ${added}${skipped ? `, skipped ${skipped}` : ""} 🍡`);
            setAddOpen(false); setText("");
            await load();
        } catch { toast.error("hmm, couldn't add those — check the emails?"); }
        finally { setSaving(false); }
    };

    const remove = async () => {
        const email = removeTarget;
        setRemoveTarget(null);
        try {
            await adminDeleteAllowlist(email);
            toast.success(`removed ${email} 🍡`);
            await load();
        } catch { toast.error("couldn't remove that one"); }
    };

    if (rows === null) return <Spinner label="reading the allowlist…" />;

    return (
        <div data-testid="admin-allowlist">
            <div className="flex items-center justify-between mb-4">
                <SectionTitle hint="These emails skip the code — they're beta the moment they sign up.">
                    Email allowlist
                </SectionTitle>
                <button
                    onClick={() => setAddOpen(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover transition-colors shrink-0"
                    data-testid="allowlist-add-btn"
                >
                    <Plus className="w-4 h-4" /> Add emails
                </button>
            </div>

            {rows.length === 0 ? (
                <Card className="p-2">
                    <EmptyState testid="allowlist-empty">
                        the allowlist is empty — add a friend's email to fast-track them 🌸
                    </EmptyState>
                </Card>
            ) : (
                <Table head={["Email", "Status", "Added", "Used", ""]} testid="allowlist-table">
                    {rows.map((r) => (
                        <tr key={r.email} className="hover:bg-surface/50 transition-colors" data-testid={`allowlist-row-${r.email}`}>
                            <td className="px-4 py-3 text-warm-900">{r.email}</td>
                            <td className="px-4 py-3"><StatusPill status={r.used ? "used" : "pending"} /></td>
                            <td className="px-4 py-3 text-warm-500">{fmtDate(r.added_at)}</td>
                            <td className="px-4 py-3 text-warm-500">{fmtDate(r.used_at)}</td>
                            <td className="px-4 py-3 text-right">
                                <button
                                    onClick={() => setRemoveTarget(r.email)}
                                    className="p-1.5 rounded-full hover:bg-terracotta-soft/40 transition-colors"
                                    aria-label={`Remove ${r.email}`}
                                    data-testid={`allowlist-remove-${r.email}`}
                                >
                                    <Trash2 className="w-4 h-4 text-terracotta" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </Table>
            )}

            <KawaiiModal open={addOpen} onClose={() => setAddOpen(false)} title="Add to the allowlist" testid="add-allowlist-modal">
                <p className="text-warm-800 text-sm mb-2">One email per line (or comma-separated).</p>
                <textarea
                    rows={5}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={"friend@example.com\nbestie@example.com"}
                    className="w-full px-4 py-3 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40 mb-6 resize-none"
                    data-testid="add-allowlist-textarea"
                />
                <div className="flex gap-3 justify-end">
                    <button onClick={() => setAddOpen(false)} className="px-4 py-2 rounded-full text-sm font-semibold text-warm-800 bg-surface hover:bg-surface-hover" data-testid="add-allowlist-cancel">Never mind</button>
                    <button onClick={add} disabled={saving} className="px-4 py-2 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover disabled:opacity-60" data-testid="add-allowlist-submit">
                        {saving ? "adding…" : "Add them"}
                    </button>
                </div>
            </KawaiiModal>

            <ConfirmDialog
                open={!!removeTarget}
                onClose={() => setRemoveTarget(null)}
                onConfirm={remove}
                title="Remove this email?"
                message="Are you sure? They'll need a code to join if they haven't signed up yet 🍡"
                confirmLabel="Remove it"
                danger
                testid="allowlist-remove-confirm"
            />
        </div>
    );
}
